import React, { useState, useEffect, lazy, Suspense } from "react";

// Essential libraries for core functionality
import { Col, Container, Navbar, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { m, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";
import { cdn } from "../utils/cdn";
import { ProtectedEmail, ProtectedPhone, INAQ_CONTACTS } from "../utils/ContactObfuscator";

// Components needed for initial viewport
import HeaderLanding, { HeaderNav } from "../Components/Header/HeaderLanding";

// Lazy-loaded components (not needed for initial render)
const ParticleSection = lazy(() => import("../Components/ParticleSection"));
const Footer = lazy(() => import("../Components/Footer"));
const ModalPopupContact = lazy(() => import("../Components/ModalPopupContact"));
const Buttons = lazy(() => import("../Components/Button/Buttons"));
const BentoGrid = lazy(() =>
  import("../Components/BentoGrid").then((module) => ({
    default: module.BentoGrid,
  }))
);
const BentoGridItem = lazy(() =>
  import("../Components/BentoGrid").then((module) => ({
    default: module.BentoGridItem,
  }))
);

// Icons in  SVG format

const EyeIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 576 512'
    fill='currentColor'
    width='1em'
    height='1em'
  >
    {/* This is the path data from Font Awesome's Eye icon */}
    <path d='M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z' />
  </svg>
);
const STDCEyeIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 24 24'
    fill='currentColor'
    width='1em'
    height='1em'
    aria-hidden='true'
    className='h-6 w-6 text-inaqpurple'
  >
    {/* Simplified eye path that will match better with the STDC section style */}
    <path d='M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z' />
  </svg>
);

const CreditCardIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 256 256'
    fill='currentColor'
    width='1em'
    height='1em'
  >
    <path d='M224,48H32A16,16,0,0,0,16,64V192a16,16,0,0,0,16,16H224a16,16,0,0,0,16-16V64A16,16,0,0,0,224,48ZM136,168H72a8,8,0,0,1,0-16h64a8,8,0,0,1,0,16Zm56,0H176a8,8,0,0,1,0-16h16a8,8,0,0,1,0,16ZM32,112V88a8,8,0,0,1,8-8H216a8,8,0,0,1,8,8v24Z' />
  </svg>
);
const LightbulbIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 384 512'
    fill='currentColor'
    width='1em'
    height='1em'
    aria-hidden='true'
    className='h-6 w-6 text-inaqpurple'
  >
    {/* Path data for Font Awesome's lightbulb icon */}
    <path d='M272 384c9.6-31.9 29.5-59.1 49.2-86.2l0 0c5.2-7.1 10.4-14.2 15.4-21.4c19.8-28.5 31.4-63 31.4-100.3C368 78.8 289.2 0 192 0S16 78.8 16 176c0 37.3 11.6 71.9 31.4 100.3c5 7.2 10.2 14.3 15.4 21.4l0 0c19.8 27.1 39.7 54.4 49.2 86.2H272zM192 512c44.2 0 80-35.8 80-80V416H112v16c0 44.2 35.8 80 80 80zM112 176c0 8.8-7.2 16-16 16s-16-7.2-16-16c0-61.9 50.1-112 112-112c8.8 0 16 7.2 16 16s-7.2 16-16 16c-44.2 0-80 35.8-80 80z' />
  </svg>
);

const CreditCardFrontFillIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 576 512'
    fill='currentColor'
    width='1em'
    height='1em'
    aria-hidden='true'
    className='h-5 w-6 text-inaqpurple'
  >
    {/* Path data for Bootstrap's CreditCard2FrontFill icon */}
    <path d='M64 32C28.7 32 0 60.7 0 96v320c0 35.3 28.7 64 64 64H512c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zm80 256h64c8.8 0 16 7.2 16 16s-7.2 16-16 16H144c-8.8 0-16-7.2-16-16s7.2-16 16-16zm128 0H368c8.8 0 16 7.2 16 16s-7.2 16-16 16H272c-8.8 0-16-7.2-16-16s7.2-16 16-16zM144 224h64c8.8 0 16 7.2 16 16s-7.2 16-16 16H144c-8.8 0-16-7.2-16-16s7.2-16 16-16zm128 0h96c8.8 0 16 7.2 16 16s-7.2 16-16 16H272c-8.8 0-16-7.2-16-16s7.2-16 16-16zm-128-64h224c8.8 0 16 7.2 16 16s-7.2 16-16 16H144c-8.8 0-16-7.2-16-16s7.2-16 16-16z' />
  </svg>
);

const LoyaltyIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 24 24'
    fill='currentColor'
    width='1em'
    height='1em'
    aria-hidden='true'
    className='h-6 w-6 text-inaqpurple'
  >
    {/* Path data for Material Design's Loyalty icon */}
    <path d='M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7zm11.77 8.27L13 19.54l-4.27-4.27A2.5 2.5 0 0 1 8 13.24l3-3a2.5 2.5 0 0 1 2.83-.39l4.27 4.27c.3.3.3.79 0 1.09z' />
  </svg>
);

const CheckCircleIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 512 512'
    fill='currentColor'
    width='1em'
    height='1em'
    className='text-gray-500 text-xl flex-shrink-0 group-hover:text-[#6a604d]'
  >
    {/* Path data for Font Awesome's check-circle icon */}
    <path d='M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z' />
  </svg>
);

// Styles
import "../styles/marketingLanding.css";

const MarketingPrePodlahy = (props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showCrossout, setShowCrossout] = useState(false);
  const [showReplacement, setShowReplacement] = useState(false);
  const [showAdditionalContent, setShowAdditionalContent] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // Handle scroll behavior for header visibility
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      // Hide header at the top of the page
      if (currentScrollY <= 0) {
        setHeaderVisible(false);
        setLastScrollY(currentScrollY);
        return;
      }
      // Show header when scrolling up, hide when scrolling down
      if (currentScrollY < lastScrollY) {
        setHeaderVisible(true);
      } else {
        setHeaderVisible(false);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);

  // Text replacement animation effect
  useEffect(() => {
    // Wait until after the main image is loaded before starting animations
    if (isImageLoaded) {
      const crossoutTimer = setTimeout(() => {
        setShowCrossout(true);
      }, 2000); // Reduced from 3000ms

      const replacementTimer = setTimeout(() => {
        setShowReplacement(true);
      }, 3500); // Reduced from 5000ms

      const additionalContentTimer = setTimeout(() => {
        setShowAdditionalContent(true);
      }, 4000); // Reduced from 6000ms

      return () => {
        clearTimeout(crossoutTimer);
        clearTimeout(replacementTimer);
        clearTimeout(additionalContentTimer);
      };
    }
  }, [isImageLoaded]); // Only run after image is loaded

  useEffect(() => {
    // Add a class to indicate loading state
    document.documentElement.classList.add("js-loading");

    // Define a timeout to ensure text displays even if fonts fail
    const timeout = setTimeout(() => {
      document.documentElement.classList.remove("js-loading");
    }, 1000);

    // Use Font Loading API if available
    if ("fonts" in document) {
      Promise.all([
        document.fonts.load("700 1em Oxanium"),
        document.fonts.load("400 1em Rubik"),
      ]).then(() => {
        clearTimeout(timeout);
        document.documentElement.classList.remove("js-loading");
      });
    }

    return () => clearTimeout(timeout);
  }, []);

  const fadeIn2 = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.2 }, // 200ms
  };
  const shouldReduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const fadeInWhenVisible = {
    initial: { opacity: 0 },
    whileInView: { opacity: 1 },
    viewport: { once: true, margin: "100px" },
    transition: {
      duration: shouldReduceMotion ? 0.1 : 0.2,
      ease: "easeOut",
    },
  };

  return (
    <>
      <Helmet>
        {/* Fonts are inlined in public/index.html - no external requests needed */}
      </Helmet>
      <div
        className='inaqpage marketing-landing overflow-x-hidden max-w-full'
        style={props.style}
      >
        {/* Header Start */}
        <HeaderLanding
          topSpace={{ md: true }}
          type='reverse-scroll'
          width='100%'
          className={`w-full bg-inaqpurple fixed transition-all duration-300 block md:hidden ${headerVisible ? "translate-y-0" : "-translate-y-full"}`}
        >
          {" "}
          <HeaderNav
            theme='light'
            expand='lg'
            bg='inaqpurple'
            containerClass='sm:!px-0'
            className='py-[0px] px-[35px] md:pr-[15px] md:pl-[10px] md:py-[12px] max-w-[1280px] mx-auto h-[70px]'
            fluid='fluid'
          >
            <Col sm={6} lg={2} xs='auto' className='me-auto ps-0'>
              <Link
                aria-label='logo home'
                className='flex items-center'
                to='/'
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                <Navbar.Brand className='inline-block p-0 m-0'>
                  <img
                    className='default-logo'
                    width='111'
                    height='36'
                    loading='lazy'
                    src='/assets/img/webp/inaq_logo_web.webp'
                    data-rjs='/assets/img/webp/inaq_logo_web.webp'
                    alt='logo'
                  />
                  <img
                    className='mobile-logo'
                    width='111'
                    height='36'
                    loading='lazy'
                    src='/assets/img/webp/inaq_logo_web.webp'
                    data-rjs='/assets/img/webp/inaq_logo_web.webp'
                    alt='logo'
                  />
                </Navbar.Brand>
              </Link>
            </Col>
            <Navbar.Collapse className='col-auto justify-center menu-order inaq-header-menu px-lg-0 col-lg-8'>
              <div className="font-['Oxanium'] text-white text-center w-full">
                <a
                  href='/'
                  className='text-white no-underline hover:text-white'
                >
                  B2B marketingová agentúra
                </a>
              </div>
            </Navbar.Collapse>
            <Col xs='auto' lg={2} className='text-right pe-0'>
              <Buttons
                type='button'
                onClick={() => setIsModalOpen(true)}
                className='!rounded-[2px] font-medium font-rubik uppercase md:hidden hover:text-inaqgold'
                themeColor={["#fff", "#fff"]}
                size='xs'
                backgroundColor='transparent'
                color='#212529'
                title='Formulár za 60 sekúnd'
              />
            </Col>
          </HeaderNav>
        </HeaderLanding>
        {/* Header End */}
        <ModalPopupContact
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
        {/* Add spacing to compensate for fixed header when visible */}
        <div className='h-[70px]'></div>
        <m.section
          className='py-32 md:py-12 xs:pt-0 text-left md:text-center bg-white relative overflow-visible hero-section-container'
          {...fadeIn2}
        >
          <Container className='overflow-visible'>
            <Row className='items-center overflow-visible '>
              <Col lg={7} md={12} className='mb-12 mb-lg-0 overflow-visible '>
                <h1 className='font-["Oxanium"] text-left md:text-center text-3xl sm:text-5xl xs:!text-[44px] lg:text-6xl font-bold md:!px-0 mb-6 text-gray-900'>
                  Máte návštevníkov, ale{" "}
                  <span className='inline-flex flex-col overflow-hidden relative'>
                    {/* Container for both phrases */}
                    <div className='relative h-auto'>
                      {/* Use a single AnimatePresence to manage both elements */}
                      <AnimatePresence mode='wait'>
                        {!showReplacement ? (
                          <m.span
                            key='original'
                            className='inline-block'
                            initial={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.7 }}
                          >
                            málo zákazníkov?
                            {showCrossout && (
                              <svg
                                className='absolute top-1/2 left-0 w-full h-8 -translate-y-1/2 z-10 overflow-visible'
                                xmlns='http://www.w3.org/2000/svg'
                                viewBox='0 0 400 10'
                                style={{ willChange: "transform" }} // Hint to browser about animation
                              >
                                <m.path
                                  d='M0,10 C125,-10 275,35 400,-5'
                                  fill='none'
                                  stroke='#991b1b'
                                  strokeWidth='3'
                                  strokeLinecap='butt'
                                  initial={{ pathLength: 0 }}
                                  animate={{ pathLength: 1 }}
                                  transition={{
                                    duration: 1.2,
                                    ease: "easeOut",
                                    delay: 0.2,
                                  }}
                                  style={{
                                    transform: "translateZ(0)", // Force GPU acceleration
                                    willChange: "stroke-dashoffset", // Hint to browser
                                  }}
                                />
                              </svg>
                            )}
                          </m.span>
                        ) : (
                          <m.span
                            key='replacement'
                            className='text-red-800 inline-block'
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{
                              duration: 1,
                              ease: "easeIn",
                            }}
                          >
                            zlý marketing?
                          </m.span>
                        )}
                      </AnimatePresence>
                    </div>
                  </span>
                </h1>
                <h2 className='text-lg text-gray-600 mb-6 mr-10 md:!mx-auto'>
                  Zjednotíme váš marketing a privedieme na predajňu relevantných
                  návštevníkov. Zvýšime vám predaje. Dlhodobo.
                </h2>

                {/* Reserved space for animated content to prevent layout shift */}
                <div className='min-h-[160px] md:flex md:mx-auto  justify-center'>
                  {/* Green circles with points - appear after text change */}
                  {showReplacement && (
                    <div
                      className='flex flex-col gap-3 mt-0 mb-8 '
                      style={{
                        animation: "fadeInFromTop 1s ease",
                        animationDelay: "2s",
                        animationFillMode: "both",
                        opacity: 0,
                        transform: "translateY(-10px)",
                      }}
                    >
                      <div className='flex items-center gap-2 group hover:text-[#6a604d] md:mt-4'>
                        <CheckCircleIcon />
                        <span className='text-gray-500 group-hover:text-[#6a604d] pointer-events-none'>
                          Sme skúsení marketingoví experti
                        </span>
                      </div>
                      <div className='flex items-center gap-2 group hover:text-[#6a604d]'>
                        <CheckCircleIcon />
                        <span className='text-gray-500 group-hover:text-[#6a604d] pointer-events-none'>
                          Poznáme podrobne segment podláh
                        </span>
                      </div>
                      <div className='flex items-center gap-2 group hover:text-[#6a604d]'>
                        <CheckCircleIcon />
                        <span className='text-gray-500 group-hover:text-[#6a604d] pointer-events-none'>
                          Pracujeme maximálne transparentne
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className='flex flex-wrap gap-4 min-h-[40px] md:mx-auto md:justify-center'>
                  {showAdditionalContent && (
                    <Link
                      to='/#inaq-marketing-services'
                      onClick={(e) => {
                        e.preventDefault();
                        const element = document.getElementById(
                          "inaq-marketing-services"
                        );
                        if (element) {
                          element.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                      className='inline-flex items-center text-gray-900 font-semibold  hover:text-[#6a604d] transition-colors group text-lg'
                      style={{
                        animation: "fadeInFromTop 1s ease",
                        animationFillMode: "both",
                        animationDelay: "2.5s",
                        opacity: 0,
                        transform: "translateY(-20px)",
                      }}
                    >
                      Zistite, ako vieme pomôcť{" "}
                      <span className='ml-2 inline-block transition-transform duration-200 group-hover:translate-x-1'>
                        ⇾
                      </span>
                    </Link>
                  )}
                </div>
              </Col>
              <Col lg={5} md={12} className='overflow-visible'>
                <div className='image-container relative'>
                  {/* Low quality placeholder image with blur effect */}
                  <div
                    className='absolute inset-0 transition-opacity duration-50'
                    style={{
                      opacity: isImageLoaded ? 0.5 : 1,
                      backgroundColor: "white",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  ></div>

                  {/* High quality image that replaces the placeholder */}
                  <img
                    src='https://storage.googleapis.com/inaq_sk_pictures_landing_pages/assets/img/landing/podlahy/marketingova-agentura-podlahove-studio-predajna-s-podlahami-iq.avif'
                    alt='marketingova agentura podlahy predajna showroom s podlahami'
                    className='w-full h-auto rounded-xs object-cover relative z-10 transition-opacity duration-50'
                    width='458'
                    height='458'
                    loading='eager'
                    fetchpriority='high'
                    decoding='async'
                    style={{
                      opacity: isImageLoaded ? 1 : 0,
                    }}
                    onLoad={() => setIsImageLoaded(true)}
                  />

                  {/* Floating boxes and connecting line - unchanged */}
                  <div className='floating-box floating-box-left'>
                    <div className='flex items-center gap-3'>
                      <div className='icon-circle icon-circle-primary'>
                        <EyeIcon />
                      </div>
                      <p className='text-sm font-medium m-0 text-inaqpurple'>
                        Viac relevantných návštevníkov
                      </p>
                    </div>
                  </div>
                  <div className='floating-box floating-box-bottom'>
                    <div className='flex items-center gap-3'>
                      <div className='icon-circle icon-circle-success'>
                        <CreditCardIcon />
                      </div>
                      <p className='text-sm font-medium m-0 text-black'>
                        Viac reálnych kupujúcich
                      </p>
                    </div>
                  </div>
                  <svg
                    className='connecting-line block md:hidden'
                    width='100%'
                    height='100%'
                    viewBox='0 0 100 100'
                    preserveAspectRatio='none'
                  >
                    <defs>
                      <filter
                        id='pencilTexture'
                        x='-20%'
                        y='-20%'
                        width='140%'
                        height='140%'
                      >
                        <feTurbulence
                          type='fractalNoise'
                          baseFrequency='0.02'
                          numOctaves='2'
                          result='noise'
                        />
                        <feDisplacementMap
                          in='SourceGraphic'
                          in2='noise'
                          scale='5'
                          xChannelSelector='R'
                          yChannelSelector='G'
                        />
                        <feGaussianBlur stdDeviation='0.10' />
                      </filter>
                    </defs>
                    <path
                      d='M40,12
        Q65,15 80,20
        T85,50
        Q70,65 85,70
        T65,98'
                      stroke='white'
                      strokeWidth='1.2'
                      fill='none'
                      strokeLinecap='round'
                      filter='url(#pencilTexture)'
                    />
                  </svg>
                </div>
              </Col>
            </Row>
          </Container>
          {/* Optional background element */}
          <div className='absolute inset-y-0 right-1/2 -z-10 -mr-96 w-[200%] origin-top-right skew-x-[-30deg] bg-white  sm:-mr-80 lg:-mr-96'></div>
        </m.section>
        {/* Hero Section End */}
        <m.section
          id='inaq-marketing-services'
          className='pt-24 sm:py-20 xs:py-16 px-6 md:px-4 sm:px-5'
          style={{ contentVisibility: "auto", containIntrinsicSize: "0 500px" }}
          {...fadeInWhenVisible}
        >
          <Container>
            <div className='mx-auto text-center mb-16 sm:mb-12 xs:mb-10'>
              <h1 className='font-["Oxanium"] text-center sm:text-5xl lg:text-6xl font-bold !pr-0 mb-6 !text-inaqgold relative'>
                Komplexné riešenie. Naozaj.
              </h1>
              <h2 className='mt-2 text-3xl font-normal tracking-tight !text-inaqpurple sm:text-2xl xs:text-xl'>
                Všetko, čo potrebujete pre úspešný marketing podláh
              </h2>
              <p className='mt-6 text-base leading-7 text-gray-500 max-w-3xl mx-auto xs:text-sm'>
                Ponúkame ucelený marketing pre podlahové štúdiá a predajne. Naše
                riešenia zvyšujú návštevnosť, konverziu a spokojnosť zákazníkov.
              </p>
            </div>
            <Suspense
              fallback={
                <div className='h-64 w-full bg-gray-100 animate-pulse'></div>
              }
            >
              <BentoGrid className='max-w-6xl mx-auto'>
                {[
                  {
                    title: "Digital first prístup",
                    description:
                      "Komplexne nastavíme a optimalizujeme digitálne marketingové kanály pre vaše podlahové štúdio.",
                    header: (
                      <div className='flex flex-1 w-full h-full min-h-[6rem] rounded-md overflow-hidden'>
                        <img
                          src={cdn(
                            "/assets/img/landing/podlahy/digital-first-strategy.avif"
                          )}
                          alt='Digital first prístup'
                          className='w-full h-full object-cover'
                          loading='lazy'
                          decoding='async'
                        />
                      </div>
                    ),
                    className: "",
                  },
                  {
                    title: "SEO pre podlahy",
                    description:
                      "Zlepšíme vašu viditeľnosť na internete a privedieme zákazníkov, ktorí prejavujú aktívny záujem o podlahy.",
                    header: (
                      <div className='flex flex-1 w-full h-full min-h-[6rem] rounded-md overflow-hidden'>
                        <img
                          src={cdn(
                            "/assets/img/landing/podlahy/socialne-siete-marketing-podlahy-predajne.avif"
                          )}
                          alt='SEO pre podlahy'
                          className='w-full h-full object-cover'
                          loading='lazy'
                          decoding='async'
                        />
                      </div>
                    ),
                    className: "",
                  },
                  {
                    title: "UX dizajn",
                    description:
                      "Preveríme vašu webstránku a zlepšíme užívateľskú skúsenosť. Zlepšíme konverziu a zdvihneme predaje. ",
                    header: (
                      <div className='flex flex-1 w-full h-full min-h-[6rem] rounded-md overflow-hidden'>
                        <img
                          src={cdn(
                            "/assets/img/landing/podlahy/marketing-podlahy-fotenie-realizacii.avif"
                          )}
                          alt='UX dizajn pre podlahy'
                          className='w-full h-full object-cover'
                          loading='lazy'
                          decoding='async'
                        />
                      </div>
                    ),
                    className: "",
                  },
                  {
                    title: "Online marketing",
                    description:
                      "Auditujeme súčasný rozpočet a alokáciu na PPC reklamy, sociálne siete a obsahový marketing. Vytlačíme maximum za vami investované peniaze.",
                    header: (
                      <div className='flex flex-1 w-full h-full min-h-[6rem] rounded-md overflow-hidden'>
                        <img
                          src={cdn(
                            "/assets/img/landing/podlahy/marketing-showroom-podlahy.avif"
                          )}
                          alt='Marketing showroom podlahy'
                          className='w-full h-full object-cover'
                          loading='lazy'
                          decoding='async'
                        />
                      </div>
                    ),
                    className: "col-span-2 sm:col-span-1",
                  },
                  {
                    header: (
                      <div className='!p-0 flex-1 w-full h-full min-h-[6rem] rounded-md flex items-center justify-center text-white flex-col bento-campaign-number py-auto relative'>
                        {/* Custom background with multiple radial gradients */}
                        <div
                          className='absolute inset-0 rounded-md'
                          style={{
                            background:
                              "radial-gradient(ellipse at top left, #241025 40%, transparent 70%), radial-gradient(circle at bottom right, #1C0C1D 30%, #241025 40%)",
                            zIndex: 0,
                          }}
                        ></div>

                        {/* Grain overlay */}
                        <div className='absolute inset-0 opacity-40 mix-blend-soft-light pointer-events-none'>
                          <div className='h-full w-full bg-noise'></div>
                        </div>

                        <div className='flex flex-col items-center justify-center gap-y-1 px-6 text-center'>
                          <span className='text-[96px] lg:text-[80px] md:text-[52px] font-bold mb-1 relative z-10'>
                            160+
                          </span>
                          <span className='text-lg relative z-10 leading-8 md:text-md'>
                            zrealizovaných marketingových kampaní za 12 rokov
                          </span>
                        </div>
                      </div>
                    ),
                    className: "",
                  },
                  {
                    title: "Retail marketing",
                    description:
                      "Zjednotíme tlačoviny, POS materiály a všetko, s čím prichádza zákazník u vás na predajni do kontaktu. Tak, aby vaša komunikácia hrala offline aj online rovnako.",
                    header: (
                      <div className='flex flex-1 w-full h-full min-h-[6rem] rounded-md overflow-hidden'>
                        <img
                          src={cdn(
                            "/assets/img/landing/podlahy/marketingove-materialy-podlaha-predajna-podlah-grafika-tlac.avif"
                          )}
                          alt='Lokálny marketing pre podlahy'
                          className='w-full h-full object-cover'
                          loading='lazy'
                          decoding='async'
                        />
                      </div>
                    ),
                    className: "",
                  },
                  {
                    title: "Marketingová stratégia",
                    description:
                      "Navrhneme komplexný plán na zvýšenie predaja a budovanie značky vášho podlahového štúdia. Cheme, aby ste boli profitabilní krátkodobo, no budovali si dlhodobú a stabilnú rentabilitu.",
                    header: (
                      <div className='flex flex-1 w-full h-full min-h-[6rem] rounded-md overflow-hidden'>
                        <img
                          src={cdn(
                            "/assets/img/landing/podlahy/obsahovy-marketing-pre-podlahy.avif"
                          )}
                          alt='Marketingová stratégia pre podlahy'
                          className='w-full h-full object-cover'
                          loading='lazy'
                          decoding='async'
                        />
                      </div>
                    ),
                    className: "col-span-2 sm:col-span-1",
                  },
                ].map((item, i) => (
                  <BentoGridItem
                    key={i}
                    title={item.title}
                    description={item.description}
                    header={item.header}
                    className={item.className}
                  />
                ))}
              </BentoGrid>
            </Suspense>
          </Container>
        </m.section>

        {/* Rest of the content remains unchanged */}
        <m.section
          className='pt-24 sm:py-20 xs:py-16 flex justify-center px-6 md:px-4 sm:px-5'
          style={{ contentVisibility: "auto", containIntrinsicSize: "0 800px" }}
          {...fadeInWhenVisible}
        >
          <div className='max-w-[1180px] w-full flex flex-col'>
            {/* Purple section */}
            <div className=' rounded-t-md w-full px-10 !xs:px-0 lg:px-16 md:px-6 sm:px-5 py-16 sm:py-8 xs:py-6'>
              <div className='mx-auto text-left'>
                <h1 className='text-base font-semibold font-["Oxanium"] leading-7 !text-inaqgold !mb-2 '>
                  Pokrývame každú fázu
                </h1>
                <h2 className='my-0 text-3xl font-300 font-rubik tracking-tight  !text-inaqpurple sm:text-2xl'>
                  Uvedomujeme si, že 95% zákazníkov nenakupuje práve teraz.
                  Cielime aj na nich.
                </h2>
              </div>
            </div>

            {/* Gold section */}
            <div className='overflow-hidden'>
              {" "}
              <div className='relative rounded-md w-full px-10 lg:px-8 md:px-6 sm:px-5 py-16 sm:py-16 xs:py-12 overflow-hidden'>
                {/* Colored purple gradient overlay */}
                <div
                  className='absolute inset-0 rounded-md'
                  style={{
                    background:
                      "radial-gradient(ellipse at top left, #241025 20%, transparent 70%), radial-gradient(circle at bottom right, #1a0b1c 30%, #09010a 100%)",
                  }}
                >
                  <div className='grain-overlay'></div>
                </div>

                {/* Content - now relative to appear above the overlay */}
                <div className='relative z-10 mx-auto text-left mb-8 sm:mb-12'>
                  <h2 className='text-base text-center font-rubik leading-7 px-48 xs:px-2 md:px-16 mb-24 text-white !font-300 xs:text-sm xs:mb-6'>
                    Marketingové aktivity robíme v súlade s princípom modelu See
                    Think Do Care. Aké sú výzvy v jednotlivých fázach a čo
                    robíme pre ich zvládnutie?
                  </h2>
                </div>
                <dl className='relative z-10 grid grid-cols-4 lg:grid-cols-2 xs:grid-cols-1 gap-x-12 gap-y-16 lg:px-12 sm:gap-y-12 xs:gap-y-16 px-4 xs:px-8'>
                  <div className='text-left'>
                    <dt className='flex flex-col items-start gap-y-3 sm:gap-y-6'>
                      <span className='flex h-10 w-10 items-center justify-center rounded-md bg-white stdc-icon'>
                        <STDCEyeIcon
                          className='h-6 w-6 text-inaqpurple'
                          aria-hidden='true'
                        />
                      </span>
                      <span className='text-base font-semibold leading-7 text-white xs:text-sm stdc-title'>
                        Verejnosť v okolí netuší, že existujete
                      </span>
                    </dt>
                    <dd className='mt-3 text-sm leading-7 text-gray-300 xs:text-sm'>
                      Budujeme značku a komunikáciou zasahujeme okolie.
                      Vytvárame povedomie o predajni tak, aby sa v neskoršej
                      fáze vybavila v mysli záujemcov.
                    </dd>
                  </div>

                  <div className='text-left'>
                    <dt className='flex flex-col items-start gap-y-3 sm:gap-y-6'>
                      <span className='flex h-10 w-10 items-center justify-center rounded-md bg-white'>
                        <LightbulbIcon />
                      </span>
                      <span className='text-base font-semibold leading-7 text-white xs:text-sm stdc-title'>
                        Potenciálny záujemca nepríde pozrieť podlahu k vám
                      </span>
                    </dt>
                    <dd className='mt-3 text-sm leading-7 text-gray-300 xs:text-sm'>
                      Nadväzujeme na prvú fázu, ukazujeme ponuku a uľahčujeme
                      nájdenie predajne. Vysvetľujeme, prečo by bola chyba
                      nenavštíviť práve vás.
                    </dd>
                  </div>

                  <div className='text-left'>
                    <dt className='flex flex-col items-start gap-y-3 sm:gap-y-6'>
                      <span className='flex h-10 w-10 items-center justify-center rounded-md bg-white'>
                        <CreditCardFrontFillIcon />
                      </span>
                      <span className='text-base font-semibold leading-7 text-white xs:text-sm stdc-title'>
                        Návštevník u vás videl podlahu, ale nakúpil inde
                      </span>
                    </dt>
                    <dd className='mt-3 text-sm leading-7 text-gray-300 xs:text-sm'>
                      Auditujeme celý proces obsluhy na predajni, vrátane POS
                      materiálov, s ktorými zákazník odchádza. Nastavíme
                      komunikáciu po predložení ponuky.
                    </dd>
                  </div>

                  <div className='text-left'>
                    <dt className='flex flex-col items-start gap-y-3 sm:gap-y-6'>
                      <span className='flex h-10 w-10 items-center justify-center rounded-md bg-white'>
                        <LoyaltyIcon />
                      </span>
                      <span className='text-base font-semibold leading-7 text-white xs:text-sm stdc-title'>
                        Zákazník nakúpil, tým to končí. Žiadna referencia ani
                        odporúčanie
                      </span>
                    </dt>
                    <dd className='mt-3 text-sm leading-7 text-gray-300 xs:text-sm'>
                      Maximalizujeme potenciál spokojných zákazníkov. Motivujeme
                      k referencii, a tiež silnému predajnému kanálu - word of
                      mouth.
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </m.section>
        <m.section
          style={{ contentVisibility: "auto", containIntrinsicSize: "0 600px" }}
        >
          {/* FAQ Section Start */}
          <div className='bg-white'>
            <div className='px-6 py-12 mx-auto max-w-7xl divide-y divide-gray-900/10 md:py-32 xxxl:px-8 xxxl:py-40 sm:px-12'>
              <h1 className='text-2xl font-bold leading-10 tracking-tight !text-inaqgold font-["Oxanium"]'>
                Časté otázky
              </h1>
              <dl className='mt-10 space-y-8 divide-y divide-gray-900/10 pointer-events-none'>
                {[
                  {
                    id: 1,
                    question:
                      "Pre aký typ predajne s podlahami sú vaše služby vhodné?",
                    answer:
                      "V princípe pre akýkoľvek. Avšak najviac z našej práce vyťažia väčšie predajne, ktoré už fungujú na trhu. My im pomôžeme celý marketing ujednotiť, vizuálne aj funkčne. Ideálne je, ak máte širší sortiment. To nám umožní nadefinovať profily zákazníkov a ich cestu.",
                  },
                  {
                    id: 2,
                    question:
                      "Máte reálnu skúsenosť s marketingom pre predajňu podláh?",
                    answer:
                      "Áno. Vďaka tomu sme zorientovaní v ponuke. Vieme, aké sú najčastejšie bariéry pre rozhodnutie o nákupe zo strany návštevníka predajne. Máme technické znalosti a vieme na nich postaviť marketingové cesty. Vieme, ktorý sortiment komu ponúkať.  ",
                  },
                  {
                    id: 3,
                    question: "Viete mi spraviť web alebo e-shop?",
                    answer: [
                      "Máme technické znalosti na tvorbu, nie je to ale naša primárna oblasť špecializácie. Vieme vám zoptimalizovať procesy na webe smerujúce k návšteve predajne. Kampane pre nákup v e-shope prenecháme špecializovaným spoločnostiam.",
                      "Určite ale viete, že v drvivej väčšine prebieha nákup na predajni, alebo minimálne je návšteva súčasťou. My preto cielime na vyladenie návštevy tak, aby ste zo záujemcov spravili zákazníkov.",
                    ],
                  },
                  {
                    id: 4,
                    question:
                      "Vaše služby vyjdú skoro rovnako ako náklad na full-time zamestnanca. Prečo mám dôverovať vám?",
                    answer:
                      "Máte prístup k expertíze naprieč množstvom oblastí. A skúsenosti, ktoré zväčša za priemerný náklad na full-time marketéra v oblasti podláh nezoženiete. A ak áno, potom narovinu - nepotrebujete nás.",
                  },
                  {
                    id: 5,
                    question: "Budem musieť podpísať dlhodobú zmluvu?",
                    answer:
                      "Nie. Začatie spolupráce zvažujeme dôkladne a dlhšie, to nám umožňuje ukončovať v prípade nespokojnosti rýchlo. Je to tak najlepšie pre obe strany. Zároveň je ale našim cieľom budovať dlhodobé spolupráce, z ktorých vyťažia obe strany.",
                  },
                ].map((faq) => (
                  <div
                    key={faq.id}
                    className='pt-8 grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 xxxl:grid-cols-12 xxxl:gap-8 md:gap-4'
                  >
                    <dt className='text-base font-semibold leading-12 text-gray-900 mb-2 md:mb-0 xxxl:mb-0 xxxl:col-span-5 stdc-title'>
                      {faq.question}
                    </dt>
                    <dd className='mt-0 xxxl:col-span-7 xxxl:mt-0 '>
                      {Array.isArray(faq.answer) ? (
                        faq.answer.map((paragraph, index) => (
                          <p
                            key={index}
                            className={`text-base  text-gray-600 ${index > 0 ? "mt-4" : ""}`}
                          >
                            {paragraph}
                          </p>
                        ))
                      ) : (
                        <p className='text-base  text-gray-600'>{faq.answer}</p>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
          {/* FAQ Section End */}
        </m.section>
        {/* CTA Start */}
        <m.section
          style={{ contentVisibility: "auto", containIntrinsicSize: "0 400px" }}
        >
          <Suspense
            fallback={
              <div className='h-[400px] bg-gray-100 animate-pulse rounded-md'></div>
            }
          >
            <ParticleSection className='particle-section'>
              <div className='mx-auto text-center py-40 sm:py-32 xs:pb-10'>
                <h1 className='font-["Oxanium"] text-center sm:text-5xl lg:text-6xl font-bold !pr-0 mb-6 !text-white relative pointer-events-none'>
                  Vyladíme vašu komunikáciu
                </h1>
                <p className='mt-16 xs:mt-12 mb-8 xs:mb-12 text-base leading-8 md:leading-10 text-gray-300 max-w-3xl mx-auto xs:text-[14px] xs:px-6 pointer-events-none'>
                  Máte dnes marketing nastavený tak, že neprichádzate o
                  zákazníkov? Vyplňte krátky formulár a spolu pozrieme na to,
                  ako vám vieme pomôcť.
                </p>

                <Buttons
                  type='button'
                  onClick={() => setIsModalOpen(true)}
                  className='rounded-[2px] bg-white font-normal font-rubik uppercase shadow-[0_0_30px_rgba(0,0,0,0.08)] text-inaqpurple
        hover:bg-inaqgold active:bg-inaqgold text-lg py-3 px-6'
                  size='lg'
                  backgroundColor='#660099!important'
                  color='#eaeaea'
                  title='Otvoriť formulár'
                />
                <p className='mt-8 xs:mt-12 xs:mb-12 xs:px-16 text-base leading-8 md:leading-10 text-gray-300 max-w-3xl mx-auto xs:text-[14px]'>
                  Môžete nám tiež zavolať na{" "}
                  <ProtectedPhone
                    phone={INAQ_CONTACTS.PHONE_TEL}
                    display={INAQ_CONTACTS.PHONE_DISPLAY}
                    className='font-bold hover:underline'
                  />{" "}
                  alebo napísať na{" "}
                  <ProtectedEmail
                    email={INAQ_CONTACTS.MARKETING_EMAIL}
                    className='font-bold hover:underline'
                  />
                </p>
              </div>
            </ParticleSection>
          </Suspense>
        </m.section>
        {/* CTA End */}
        {/* Footer Start */}

        <Suspense
          fallback={<div className='h-[200px] bg-gray-200 animate-pulse'></div>}
        >
          <Footer
            className='inaq-footer'
            theme='light'
            logo='/assets/img/webp/inaq_logo.svg'
            width='40'
            objectFit='contain'
            loading='lazy'
          />
        </Suspense>
        {/* Footer End */}
      </div>
      <style>{`
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes fadeInFromTop {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`}</style>
    </>
  );
};

export default MarketingPrePodlahy;
